import { Check, Clear, Loop } from '@mui/icons-material';
import { Box } from '@mui/material';
import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AbsenceEntry,
  AbsenceStatus,
  AbsenceSupersededReason,
} from '~/data/absences/types';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { getFormattedDateString } from '~/utils/helpers/date';

import { useSxStyles } from './useStyles';

export type AbsenceStatusCellProps = {
  status: AbsenceStatus;
  absenceEntry: AbsenceEntry;
  canApproveAbsence: boolean;
  isHistory?: boolean;
  decisionActions?: (absenceId: string) => ReactElement;
};

export const AbsenceStatusCell = (props: AbsenceStatusCellProps) => {
  const {
    status,
    absenceEntry: {
      decidedDate,
      deciderName,
      supersededReason,
      supersededName,
      supersededDate,
      uid: absenceId,
    },
    canApproveAbsence,
    isHistory,
    decisionActions,
  } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const { getEnums } = useAllEnums();

  const statusEnum = useMemo(() => {
    const statuses = getEnums(EnumListKey.ABSENCE_STATUS);

    return statuses.find((s) => s.code === status);
  }, [getEnums, status]);

  const getStatusIcon = useCallback((status: string | AbsenceStatus) => {
    switch (status) {
      case AbsenceStatus.PROPOSED:
        return undefined;
      case AbsenceStatus.APPROVED:
        return <Check color={'success'} />;
      case AbsenceStatus.CHANGES_REQUESTED:
        return <Loop color={'info'} />;
      case AbsenceStatus.REJECTED:
      case 'DELETE':
        return <Clear color={'error'} />;
      default:
        return undefined;
    }
  }, []);

  const getStatusText = useCallback(
    (_status: string, name?: string, date?: Date) => {
      return `${name} ${getFormattedDateString(date, 'L')}`;
    },
    [],
  );

  const AbsenceState = (props: { icon?: ReactNode; children?: ReactNode }) => {
    const { icon, children } = props;

    return (
      <Box sx={sxStyles.status}>
        {icon}
        <Box>{children ?? statusEnum?.name}</Box>
      </Box>
    );
  };

  const AbsenceEntryDecision = () => {
    return (
      <AbsenceState icon={getStatusIcon(status)}>
        {getStatusText(statusEnum?.name ?? status, deciderName, decidedDate)}
      </AbsenceState>
    );
  };

  if (supersededReason === AbsenceSupersededReason.DELETE) {
    return (
      <AbsenceState icon={getStatusIcon(supersededReason)}>
        {getStatusText(t('laDeleted'), supersededName, supersededDate)}
      </AbsenceState>
    );
  }

  switch (status) {
    case AbsenceStatus.PROPOSED:
      if (isHistory) {
        return <></>;
      }

      if (!!decisionActions && absenceId && canApproveAbsence) {
        return decisionActions(absenceId);
      }

      return (
        <AbsenceState icon={getStatusIcon(status)}>
          {t('laWaitApproval')}
        </AbsenceState>
      );
    case AbsenceStatus.CHANGES_REQUESTED:
    case AbsenceStatus.APPROVED:
    case AbsenceStatus.REJECTED:
      return <AbsenceEntryDecision />;
    case AbsenceStatus.DRAFT:
    default: {
      return <AbsenceState />;
    }
  }
};
